import { observer } from 'mobx-react-lite';
import { isFeatureEnabled } from 'owa-feature-flags';
import { UncontrolledExtendedRecipientWell } from 'owa-readwrite-recipient-well-fabric';
import { RecipientWell } from 'owa-readwrite-recipient-well-fabric/lib/components/PeoplePickerTypes';
import { useRecipientWellRegistration } from 'owa-readwrite-recipient-well-fabric/lib/store/registry';
import removeReadWriteRecipients from 'owa-readwrite-recipient-well/lib/actions/removeReadWriteRecipients';
import addRecipientsToRecipientWell from 'owa-recipient-common/lib/actions/addRecipientsToRecipientWell';
import { lazyReorderRecipientsInRecipientWell } from 'owa-unified-people-picker';
import removeFindPeopleEntity from 'owa-recipient-common/lib/utils/removeFindPeopleEntity';
import ReadWriteRecipientViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientViewState';
import { UncontrolledRecipientWellProps } from 'owa-readwrite-recipient-well-fabric/lib/store/schema/UncontrolledRecipientWellProps';
import { UncontrolledUnifiedRecipientWell } from 'owa-unified-people-picker';
import * as React from 'react';

// This component is a wrapper to move logic to UnifiedPeoplePicker
// when the 'mon-rp-unifiedPicker' feature flag is enabled
const UncontrolledRecipientWellInner = (
    props: UncontrolledRecipientWellProps,
    originalRef: React.Ref<RecipientWell>
) => {
    const ref = useRecipientWellRegistration(originalRef, props.viewState.recipientWellId);

    const onItemsAdded = React.useCallback(
        (recipients: ReadWriteRecipientViewState[]) => {
            addRecipientsToRecipientWell(recipients, props.viewState);

            props.onItemsAdded?.(recipients);
        },
        [props.viewState]
    );

    const onItemsRemoved = React.useCallback(
        (recipients: ReadWriteRecipientViewState[], fromExpand?: boolean) => {
            removeReadWriteRecipients(props.viewState, recipients, !fromExpand);
            recipients.forEach(recipient => {
                removeFindPeopleEntity(props.viewState.findPeopleFeedbackManager, recipient);
            });
            props.onItemsRemoved?.(recipients, fromExpand);
        },
        [props.viewState]
    );

    const dropItemsAt = React.useCallback(
        (
            insertIndex: number,
            newItems: ReadWriteRecipientViewState[],
            indicesToRemove: number[]
        ) => {
            lazyReorderRecipientsInRecipientWell.importAndExecute(
                props.viewState,
                insertIndex,
                newItems,
                indicesToRemove
            );
        },
        [props.viewState]
    );

    return isFeatureEnabled('mon-rp-unifiedPicker') ? (
        <UncontrolledUnifiedRecipientWell
            ref={ref}
            {...props}
            internalOnItemsAdded={onItemsAdded}
            internalOnItemsRemoved={onItemsRemoved}
            internalDropItemsAt={dropItemsAt}
        />
    ) : (
        <UncontrolledExtendedRecipientWell
            ref={ref}
            {...props}
            internalOnItemsAdded={onItemsAdded}
            internalOnItemsRemoved={onItemsRemoved}
        />
    );
};

/**
 * A component that renders a fabric recipient well against a viewstate,
 * and automatically updates the viewstate when the recipient well is interacted with.
 */
export const UncontrolledRecipientWell = observer(UncontrolledRecipientWellInner, {
    forwardRef: true,
});

export default UncontrolledRecipientWell;
